import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, Subscription } from 'rxjs';
import { SnackbarService } from '../../../services/snackbar.service';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { SnackbarAlert, SnackbarAlertStatus } from '../../../utils';
import { AuthService } from '../../auth.service';
import { Router } from '@angular/router';
import { AuthErrorCode, LoginResponse, SessionEndReason } from '../../auth.models';
import { DesignationEnum } from '../../../common/models/common.model';
import { RawMaterialTab } from '../../../raw-material-inventory/raw-material.model';
import { PermissionService } from '../../../services/permission.service';
import { firstAccessibleRoute } from '../../../common/navigation';

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  protected credentialFormGroup!: FormGroup;
  protected isUserLogging: boolean = false;
  protected showPassword: boolean = false;
  protected currentYear: number = new Date().getFullYear();
  protected sessionEndReason: SessionEndReason | null = null;
  protected readonly authErrorCode = AuthErrorCode;

  private _authSubscription!: Subscription;
  private snackbarRef!: MatSnackBarRef<any>;

  constructor(
    private _snackbarService: SnackbarService,
    private _authService: AuthService,
    private _router: Router,
    private _permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    // Read once, so the notice does not reappear on a later visit to this screen.
    this.sessionEndReason = this._authService.consumeSessionEndReason();
    this._initializeCredentialFormGroup();
  }

  protected onLogin(): void {
    if (this.credentialFormGroup?.invalid) {
      this.snackbarRef = this._snackbarService.show(SnackbarAlert.INVALID_FORM, SnackbarAlertStatus.ERROR);

      return;
    }

    this.isUserLogging = true;
    this.sessionEndReason = null;
    this._authSubscription = this._authService
      .login({
        ...this.credentialFormGroup.value,
        client_type: 'admin',
      })
      .pipe(
        finalize((): void => {
          this.isUserLogging = false;
        })
      )
      .subscribe({
        next: (response: LoginResponse): void => {
          localStorage.setItem('auth_token', response.access);
          localStorage.setItem('refresh_token', response.refresh);

          // Set to true again for the duration of the initializeSession call
          this.isUserLogging = true;
          this._authService
            .initializeSession()
            .pipe(
              finalize(() => {
                // This finalize ensures isUserLogging is false after the *initializeSession* API call
                this.isUserLogging = false;
              })
            )
            .subscribe({
              next: (): void => {
                this._authService.setLoggedInStatus(true);
                this._navigateToLandingPage();
              },
              error: (): void => {
                void this._router.navigate(['/login']);
              },
            });
        },
        error: (err): void => {
          // The API answers with detail as a string ('No active account found with the given
          // credentials') or as a list of strings, so indexing it blindly turns the string case
          // into its first character.
          const detail = err?.error?.detail;
          const errorMessage = (Array.isArray(detail) ? detail[0] : detail) || SnackbarAlert.INVALID_CREDENTIALS;

          this.snackbarRef = this._snackbarService.show(errorMessage, SnackbarAlertStatus.ERROR);
        },
      });
  }

  /**
   * Lands the user on the first module their designation can open, in sidebar order. Admin site
   * login alone puts nothing in the sidebar, so those users go to settings, which every signed-in
   * user can reach, rather than a dashboard that would answer 403.
   */
  private _navigateToLandingPage(): void {
    const designation = this._authService.getCurrentUser()?.employee?.designation?.title;
    const hasPermission = (code: string): boolean => this._permissionService.hasPermission(code);

    if (designation === DesignationEnum.PRODUCTION_MANAGER && hasPermission('ui_raw_material.sidebar.view')) {
      // Production Manager is only allowed the Inventory tab of the raw material section
      void this._router.navigate(['/raw-material-inventory'], { queryParams: { tab: RawMaterialTab.INVENTORY } });

      return;
    }

    void this._router.navigate([firstAccessibleRoute(hasPermission) ?? '/settings']);
  }

  private _initializeCredentialFormGroup(): void {
    this.credentialFormGroup = new FormGroup({
      email: new FormControl(null, [Validators.required]),
      password: new FormControl(null, [Validators.required]),
    });
  }

  ngOnDestroy(): void {
    this._authSubscription?.unsubscribe();
    this.snackbarRef?.dismiss();
  }
}
